import { OrganizationType } from "../types/organization.type";

export const REPORT_PDF_SECTION_IDS = [
  "key_metrics",
  "waste_type_breakdown",
  "outgoing_waste_breakdown",
  "supply_per_recycler",
  "container_status",
  "kpi_targets",
] as const;

export type ReportPdfSectionId = (typeof REPORT_PDF_SECTION_IDS)[number];

export interface ReportPdfSectionDescriptor {
  id: ReportPdfSectionId;
  allowedTypes: OrganizationType[];
}

const ALL_ORG_TYPES: OrganizationType[] = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.TRUCK,
  OrganizationType.RECYCLER,
  OrganizationType.CHARITY,
];

const SITE_OPS: OrganizationType[] = [
  OrganizationType.WASTE_OUTLET,
  OrganizationType.CHARITY,
];

/**
 * PDF tables the user can include or omit when customizing a report. Kept in
 * line with the dashboard catalog: material and daily breakdowns are real for
 * every org type; supply per recycler is not calculated by the API yet (always
 * an empty list), so it is never offered.
 */
export const REPORT_PDF_SECTION_CATALOG: ReportPdfSectionDescriptor[] = [
  { id: "key_metrics", allowedTypes: ALL_ORG_TYPES },
  { id: "waste_type_breakdown", allowedTypes: ALL_ORG_TYPES },
  { id: "outgoing_waste_breakdown", allowedTypes: ALL_ORG_TYPES },
  { id: "supply_per_recycler", allowedTypes: [] },
  { id: "container_status", allowedTypes: SITE_OPS },
  { id: "kpi_targets", allowedTypes: ALL_ORG_TYPES },
];

export function getReportSectionsForOrgType(
  orgType: OrganizationType,
): ReportPdfSectionId[] {
  return REPORT_PDF_SECTION_CATALOG.filter((d) =>
    d.allowedTypes.includes(orgType),
  ).map((d) => d.id);
}

export function sanitizeReportSections(
  selected: unknown,
  orgType: OrganizationType,
): ReportPdfSectionId[] {
  const allowed = getReportSectionsForOrgType(orgType);
  if (!Array.isArray(selected)) return allowed;
  const allowedSet = new Set(allowed);
  const sanitized = selected.filter(
    (id): id is ReportPdfSectionId =>
      typeof id === "string" && allowedSet.has(id as ReportPdfSectionId),
  );
  if (selected.length === 0) return [];
  return sanitized.length > 0 ? sanitized : allowed;
}

export function isReportSectionEnabled(
  id: ReportPdfSectionId,
  includeSections?: readonly ReportPdfSectionId[] | null,
): boolean {
  if (!includeSections) return true;
  return includeSections.includes(id);
}
